import React, { useContext, useEffect, useMemo, useState } from "react";
import { compactPageItems } from "../../components/ui/compactPagination";
import api from "../../api";
import { AuthContext } from "../../context/AuthContext";
import {
  Modal,
  Button,
  Form,
  InputGroup,
  Pagination,
  Spinner,
} from "react-bootstrap";
import {
  viewAuthenticatedFile,
  downloadAuthenticatedFile,
  getFileAccessErrorMessage,
} from "../../utils/fileAccess";
import UnitChip from "../../components/common/UnitChip";
import UserChip from "../../components/common/UserChip";
import StatusChip from "../../components/common/StatusChip";
import QtyBadge from "../../components/common/QtyBadge";
import ProductTypeAvatar from "../../components/common/ProductTypeAvatar";
import "./AllocationUnitWisePage.css";
import "./TechnicianDesignSystem.css";
import TechModalTitle from "./TechModalTitle";
import AllocationPrintActions from "./AllocationPrintActions";
import {
  PRINT_KIND,
  allotmentPrintableRows,
  buildReturnSheetHTML,
  loadPrintDocument,
} from "./allocationPrintDocuments";
import {
  getMonthKey,
  buildMonthOptions,
  monthFileSuffix,
  sanitizeFileNamePart,
  csvQuote,
} from "../../utils/monthFilter";

// Native tooltip only when the text is actually cut off (single-line
// ellipsis or a multi-line clamp); fully visible text gets no tooltip.
const titleIfTruncated = (event) => {
  const el = event.currentTarget;
  const truncated =
    el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;

  if (truncated) el.setAttribute("title", el.textContent);
  else el.removeAttribute("title");
};

// Which unit the page opens on. GET /alloc/units already returns only the
// caller's own unit unless they hold cross-unit access, so for a
// unit-restricted user this is simply that one unit. A cross-unit user
// (full list) starts on their own unit rather than whichever unit sorts
// first -- the old `units[0]` default put every user on "Corporate office".
// The server list is the source of truth: a Manager always receives exactly
// their own unit, so a single-unit list is taken as-is even if the cached
// session's unit_id is stale. A multi-unit list without the own unit
// selects nothing rather than guessing.
export function pickDefaultUnitId(units, ownUnitId) {
  if (!Array.isArray(units) || units.length === 0) return "";
  const own = units.find((u) => String(u.id) === String(ownUnitId));
  if (own) return String(own.id);
  return units.length === 1 ? String(units[0].id) : "";
}

export default function AllocationUnitWisePage() {
  const { user: authUser } = useContext(AuthContext) || {};
  const [units, setUnits] = useState([]);
  const [unitSummary, setUnitSummary] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState("");

  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [allocations, setAllocations] = useState([]);

  const [userId, setUserId] = useState("");
  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  const [allocatedAt, setAllocatedAt] = useState("");
  const [allocationItems, setAllocationItems] = useState([]);

  const [userSearch, setUserSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [showUserDrop, setShowUserDrop] = useState(false);
  const [showProductDrop, setShowProductDrop] = useState(false);

  const [editing, setEditing] = useState(null);
  const [historyUser, setHistoryUser] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [brandFilter, setBrandFilter] = useState("ALL");
  const [userFilter, setUserFilter] = useState("ALL");
  const [vendorFilter, setVendorFilter] = useState("ALL");
  const [monthFilter, setMonthFilter] = useState("ALL");

  const [uploadingSignedFor, setUploadingSignedFor] = useState(null);
  const [signedFiles, setSignedFiles] = useState([]);
  const [uploadingSigned, setUploadingSigned] = useState(false);

  const [viewingSignedFor, setViewingSignedFor] = useState(null);
  const [signedDocs, setSignedDocs] = useState([]);
  const [loadingSignedDocs, setLoadingSignedDocs] = useState(false);
  const [previewSignedDoc, setPreviewSignedDoc] = useState(null);
  const [previewObjectUrl, setPreviewObjectUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(false);
  const [loadingAllocations, setLoadingAllocations] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedUnit) {
      fetchUsers(selectedUnit);
      fetchProducts(selectedUnit);
      fetchAllocationsByUnit(selectedUnit);
    } else {
      setUsers([]);
      setProducts([]);
      setAllocations([]);
    }

    setUserId("");
    setProductId("");
    setUserSearch("");
    setProductSearch("");
    setQty(1);
    setAllocationItems([]);
    setCurrentPage(1);
  }, [selectedUnit]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, brandFilter, userFilter, vendorFilter, monthFilter, rowsPerPage]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);

      const [unitRes, summaryRes] = await Promise.all([
        api.get(`/alloc/units`),
        api.get(`/alloc/unit-wise/summary`),
      ]);

      const unitData = Array.isArray(unitRes.data) ? unitRes.data : [];
      const summaryData = Array.isArray(summaryRes.data) ? summaryRes.data : [];

      setUnits(unitData);
      setUnitSummary(summaryData);

      // Keep the current selection only if it is still in the (server-
      // scoped) list; otherwise fall back to the user's own unit.
      setSelectedUnit((prev) =>
        prev && unitData.some((u) => String(u.id) === String(prev))
          ? prev
          : pickDefaultUnitId(unitData, authUser?.unit_id)
      );
    } catch (error) {
      console.error("Fetch Unit Wise Allocation Data Error:", error);
      alert("Failed to fetch unit wise allocation data");
    } finally {
      setLoading(false);
    }
  };

  const fetchUnitSummary = async () => {
    const { data } = await api.get(
      `/alloc/unit-wise/summary`
    );
    setUnitSummary(Array.isArray(data) ? data : []);
  };

  const fetchUsers = async (unitId) => {
    try {
      const { data } = await api.get(
        `/alloc/users/by-unit/${unitId}`
      );
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Fetch Users Error:", error);
      setUsers([]);
    }
  };

  const fetchProducts = async (unitId = selectedUnit) => {
    try {
      const url = unitId
        ? `/alloc/products?unit_id=${unitId}`
        : `/alloc/products`;

      const { data } = await api.get(url);
      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Fetch Products Error:", error);
      setProducts([]);
    }
  };

  const fetchAllocationsByUnit = async (unitId) => {
    try {
      setLoadingAllocations(true);

      const { data } = await api.get(
        `/alloc/unit-wise/${unitId}`
      );

      setAllocations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Fetch Unit Allocations Error:", error);
      alert("Failed to fetch unit allocations");
    } finally {
      setLoadingAllocations(false);
    }
  };

  const refreshPage = async () => {
    await fetchInitialData();

    if (selectedUnit) {
      await fetchUsers(selectedUnit);
      await fetchProducts(selectedUnit);
      await fetchAllocationsByUnit(selectedUnit);
    }
  };

  const selectedUnitInfo = useMemo(() => {
    return units.find((u) => String(u.id) === String(selectedUnit)) || null;
  }, [units, selectedUnit]);

  const selectedUser = useMemo(() => {
    return users.find((u) => String(u.id) === String(userId)) || null;
  }, [users, userId]);

  const specLine = (product) => {
    if (!product) return "-";

    const ram = product.ram_gb ? `${product.ram_gb}GB` : "-";
    const storage = product.ssd_gb
      ? `${product.ssd_gb}GB SSD`
      : product.hdd_gb
      ? `${product.hdd_gb}GB HDD`
      : "-";

    return `${product.processor || "-"} / ${ram} / ${storage}`;
  };

  // Secondary product line for the allocation table: "SKU · Model: X", plus
  // the processor/RAM/storage spec only when the product actually has one
  // (a monitor would otherwise show a meaningless "- / - / -").
  const productMetaLine = (row) => {
    const parts = [];
    if (row.sku) parts.push(row.sku);
    if (row.model_no) parts.push(`Model: ${row.model_no}`);
    if (row.processor || row.ram_gb || row.ssd_gb || row.hdd_gb) {
      parts.push(specLine(row));
    }
    return parts.join(" · ") || "-";
  };

  const safeNum = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

 const productGroups = useMemo(() => {
  return products
    .map((product) => {
      const totalQty = safeNum(product.total_qty ?? product.qty ?? 0);
      const availableQty = safeNum(product.available_qty ?? 0);

      return {
        key: String(product.id),
        id: product.id,
        name: product.name || "-",
        category: product.category || "-",
        brand: product.brand || "-",
        model_no: product.model_no || "-",
        processor: product.processor || "-",
        ram_gb: product.ram_gb,
        ssd_gb: product.ssd_gb,
        hdd_gb: product.hdd_gb,
        price: product.price,
        purchase_date: product.purchase_date,

        products: [product],
        firstProduct: product,

        serials: product.serial_no ? [product.serial_no] : [],
        skus: product.sku ? [product.sku] : [],

        serialPreview: product.serial_no || "-",
        skuPreview: product.sku || "-",

        total_qty: totalQty,
        available_qty: availableQty,
      };
    })
    .filter((item) => item.available_qty > 0)
    .sort((a, b) => {
      const bDate = new Date(
        b.firstProduct?.created_at || b.firstProduct?.createdAt || 0
      ).getTime();

      const aDate = new Date(
        a.firstProduct?.created_at || a.firstProduct?.createdAt || 0
      ).getTime();

      if (Number.isFinite(bDate) && Number.isFinite(aDate) && bDate !== aDate) {
        return bDate - aDate;
      }

      return Number(b.id || 0) - Number(a.id || 0);
    });
}, [products]);

  const selectedProductGroup = useMemo(() => {
    return productGroups.find((p) => p.key === productId) || null;
  }, [productGroups, productId]);

  const getReservedQtyByProductId = (productIdValue) => {
    return allocationItems
      .filter((item) => String(item.product_id) === String(productIdValue))
      .reduce((sum, item) => sum + Number(item.qty || 0), 0);
  };

  const getGroupCartReservedQty = (group) => {
    if (!group) return 0;

    const ids = new Set(group.products.map((product) => String(product.id)));

    return allocationItems
      .filter((item) => ids.has(String(item.product_id)))
      .reduce((sum, item) => sum + Number(item.qty || 0), 0);
  };

  const getGroupAvailableAfterCart = (group) => {
    if (!group) return 0;

    return Math.max(0, Number(group.available_qty || 0) - getGroupCartReservedQty(group));
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();

    if (!q) return users;

    return users.filter((user) =>
      [user.name, user.email, user.phone_no, user.username]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [users, userSearch]);

  const filteredProductGroups = useMemo(() => {
    const q = productSearch.trim().toLowerCase();

    const data = productGroups
      .map((group) => ({
        ...group,
        cart_reserved_qty: getGroupCartReservedQty(group),
        cart_available_qty: getGroupAvailableAfterCart(group),
      }))
      .filter((group) => group.cart_available_qty > 0);

    if (!q) return data;

    return data.filter((group) =>
      [
        group.name,
        group.category,
        group.brand,
        group.model_no,
        group.processor,
        group.serials.join(" "),
        group.skus.join(" "),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [productGroups, productSearch, allocationItems]);

 const getProductDisplay = (group) => {
  if (!group) return "";

  return `${group.skuPreview} | ${group.name} | Serial: ${
    group.serialPreview || "-"
  } | Model: ${group.model_no || "-"} | Qty: ${getGroupAvailableAfterCart(
    group
  )}`;
};

  const selectUser = (user) => {
    setUserId(String(user.id));
    setUserSearch(user.name || "");
    setShowUserDrop(false);
  };

  const selectProductGroup = (group) => {
    setProductId(group.key);
    setProductSearch(getProductDisplay(group));
    setQty(1);
    setShowProductDrop(false);
  };

  const brandOptions = useMemo(() => {
    return [
      ...new Set(
        allocations
          .map((row) => row.brand)
          .filter(Boolean)
      ),
    ].sort();
  }, [allocations]);

  const userOptions = useMemo(() => {
    return allocations
      .filter((row) => row.user_id && row.user_name)
      .reduce((acc, row) => {
        if (!acc.some((u) => String(u.id) === String(row.user_id))) {
          acc.push({ id: row.user_id, name: row.user_name });
        }
        return acc;
      }, [])
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [allocations]);

  const vendorOptions = useMemo(() => {
    return [
      ...new Set(
        allocations
          .map((row) => row.company_name)
          .filter(Boolean)
      ),
    ].sort();
  }, [allocations]);

  // Month represents when the allocation was actually issued/allocated
  // (allocations.allocated_at is the row's source-of-truth "issued" date —
  // there is no separate created_at column). A record's later status change
  // (Returned/Cancelled) never moves it out of the month it was allocated in.
  const monthOptions = useMemo(
    () => buildMonthOptions(allocations, "allocated_at"),
    [allocations]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return allocations.filter((row) => {
      const passStatus =
        statusFilter === "ALL" ? true : row.status === statusFilter;

      const passBrand =
        brandFilter === "ALL" ? true : row.brand === brandFilter;

      const passUser =
        userFilter === "ALL" ? true : String(row.user_id) === String(userFilter);

      const passVendor =
        vendorFilter === "ALL" ? true : row.company_name === vendorFilter;

      const passMonth =
        monthFilter === "ALL" ? true : getMonthKey(row.allocated_at) === monthFilter;

      if (!passStatus || !passBrand || !passUser || !passVendor || !passMonth) {
        return false;
      }

      if (!q) return true;

      const haystack = [
        row.sku,
        row.product_name,
        row.brand,
        row.unit_name,
        row.user_name,
        row.company_name,
        row.status,
        row.notes,
        row.allocated_at,
        row.return_at,
        row.model_no,
        row.serial_no,
        row.processor,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [allocations, search, statusFilter, brandFilter, userFilter, vendorFilter, monthFilter]);

  // KPI cards reflect the same filtered cohort as the table (so selecting a
  // month, optionally combined with the other filters, updates these too)
  // instead of unrelated all-time totals.
  const stats = useMemo(() => {
    return {
      totalRecords: filtered.length,
      allocated: filtered.filter((a) => a.status === "ALLOCATED").length,
      returned: filtered.filter((a) => a.status === "RETURNED").length,
      cancelled: filtered.filter((a) => a.status === "CANCELLED").length,
      allocatedQty: filtered
        .filter((a) => a.status === "ALLOCATED")
        .reduce((sum, a) => sum + Number(a.qty || 0), 0),
    };
  }, [filtered]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const pageData = filtered.slice(indexOfFirst, indexOfLast);

  const userHistoryRows = useMemo(() => {
    if (!historyUser) return [];

    return allocations.filter(
      (item) => String(item.user_id) === String(historyUser.user_id)
    );
  }, [allocations, historyUser]);

  const resetCreateForm = () => {
    setUserId("");
    setProductId("");
    setQty(1);
    setNotes("");
    setAllocatedAt(todayInputDate());
    setUserSearch("");
    setProductSearch("");
    setAllocationItems([]);
    setShowUserDrop(false);
    setShowProductDrop(false);
  };

  const openCreateModal = () => {
    if (!selectedUnit) {
      alert("Please select unit first");
      return;
    }

    resetCreateForm();
    setShowCreate(true);
  };

 const buildAllocationPayloads = (group, requestQty) => {
  if (!group) return [];

  const product = group.firstProduct || group.products?.[0];

  if (!product) return [];

  const available = Math.max(
    0,
    Number(product.available_qty || 0) - getReservedQtyByProductId(product.id)
  );

  const qtyToAllocate = Number(requestQty);

  if (qtyToAllocate > available) {
    return [];
  }

  return [
    {
      product_id: Number(product.id),
      qty: qtyToAllocate,
      notes,
      product_name: product.name || group.name || "-",
      sku: product.sku || group.skuPreview || "-",
      serial_no: product.serial_no || "-",
      model_no: product.model_no || group.model_no || "-",
      processor: product.processor || group.processor || "-",
      ram_gb: product.ram_gb || group.ram_gb,
      ssd_gb: product.ssd_gb || group.ssd_gb,
      hdd_gb: product.hdd_gb || group.hdd_gb,
    },
  ];
};

  const addAllocationItem = () => {
    if (!selectedUnit || !userId) {
      alert("Please select unit and user first");
      return;
    }

    if (!selectedProductGroup) {
      alert("Please select product");
      return;
    }

    const requestQty = Number(qty);

    if (!Number.isFinite(requestQty) || requestQty <= 0) {
      alert("Qty must be greater than 0");
      return;
    }

    const availableAfterCart = getGroupAvailableAfterCart(selectedProductGroup);

    if (requestQty > availableAfterCart) {
      alert(
        `Only ${availableAfterCart} item(s) available for ${selectedProductGroup.name}`
      );
      return;
    }

    const payloads = buildAllocationPayloads(selectedProductGroup, requestQty);

    if (!payloads.length) {
      alert("No available product item found");
      return;
    }

    setAllocationItems((prev) => [...prev, ...payloads]);
    setProductId("");
    setProductSearch("");
    setQty(1);
    setShowProductDrop(false);
  };

  const removeAllocationItem = (index) => {
    setAllocationItems((prev) => prev.filter((_, i) => i !== index));
  };


  const submit = async () => {
    if (!selectedUnit || !userId) {
      alert("Please select unit and user");
      return;
    }

    if (!allocationItems.length) {
      alert("Please add at least one product");
      return;
    }

    try {
      setSaving(true);

      await api.post(`/alloc/bulk`, {
        unit_id: Number(selectedUnit),
        user_id: Number(userId),
        notes,
        allocated_at: allocatedAt,
        items: allocationItems.map((item) => ({
          product_id: Number(item.product_id),
          qty: Number(item.qty),
          notes: item.notes || notes || "",
        })),
      });

      resetCreateForm();
      setShowCreate(false);

      await fetchProducts(selectedUnit);
      await fetchUnitSummary();
      await fetchAllocationsByUnit(selectedUnit);

      alert("Multiple products allocated successfully");
    } catch (error) {
      console.error("Bulk Allocation Error:", error);
      alert(error?.response?.data?.error || "Failed to allocate products");
    } finally {
      setSaving(false);
    }
  };

  // Re-fetching the list after a successful write is a courtesy refresh, not
  // part of the action itself -- if IT fails (e.g. a transient backend/DB
  // hiccup right after the write went through), that must never be reported
  // as "the action failed". Swallowing it here (vs. letting it bubble to the
  // caller's catch) is what keeps a real success from being misreported as a
  // failure; the list simply stays stale until the next successful refresh
  // (Refresh button / next action) instead of confusing the user with a
  // false negative that could tempt a duplicate retry of an already-applied
  // change.
  // Backend error responses on this page come in two shapes: the newer
  // {success:false, code, message} shape (middlewares/authenticate.js,
  // middlewares/authorize.js, and index.js's global error handler --
  // covers 401/403 and Multer upload rejections) and the older
  // {error: "..."} shape (controllers/allocationController.js's own catch
  // blocks). Reading only .error (as this page previously did) silently
  // swallowed every permission-denied/auth/upload-validation message and
  // showed the generic fallback instead -- .message is checked first since
  // it's the more specific, purpose-written text when both are absent.
  const getBackendErrorMessage = (error, fallback) =>
    error?.response?.data?.message || error?.response?.data?.error || fallback;

  const refreshAllocationView = async () => {
    try {
      await fetchProducts(selectedUnit);
      await fetchUnitSummary();
      await fetchAllocationsByUnit(selectedUnit);
    } catch (error) {
      console.error("Post-action refresh failed (action itself already succeeded):", error);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this allocation?")) return;

    try {
      await api.delete(`/alloc/${id}`);

      alert("Allocation deleted successfully");
      await refreshAllocationView();
    } catch (error) {
      console.error("Delete Allocation Error:", error);
      alert(getBackendErrorMessage(error, "Failed to delete allocation"));
    }
  };

  const setStatus = async (row, status) => {
    try {
      await api.put(`/alloc/${row.id}/status`, {
        status,
      });

      alert(`Status changed to ${status}`);
      await refreshAllocationView();
    } catch (error) {
      console.error("Status Update Error:", error);
      alert(getBackendErrorMessage(error, "Failed to update status"));
    }
  };

  // Dedicated Return action -- hits PUT /alloc/:id/return, not the generic
  // /status endpoint. That endpoint is authorized purely on the
  // allocations:return_product_allocation permission (data_scope='ALL' for
  // Technician/Manager/Admin), never on row.user_id === the logged-in user,
  // so this works for a product allocated to any user, not just yourself.
  const returnAllocation = async (row) => {
    try {
      await api.put(`/alloc/${row.id}/return`);

      alert("Product returned successfully");
      await refreshAllocationView();
    } catch (error) {
      console.error("Return Allocation Error:", error);
      alert(getBackendErrorMessage(error, "Failed to return product"));
    }
  };

  const getProductById = (productId) => {
  return products.find((p) => String(p.id) === String(productId)) || null;
};

const getEditQtyInfo = () => {
  if (!editing) {
    return {
      oldQty: 0,
      availableQty: 0,
      maxAllowedQty: 0,
    };
  }

  const oldQty = Number(editing.old_qty || editing.qty || 0);
  const product = getProductById(editing.product_id);

  const availableQty = Number(
    product?.available_qty ?? product?.qty ?? 0
  );

  return {
    oldQty,
    availableQty,
    maxAllowedQty: oldQty + availableQty,
  };
};

const saveEdit = async () => {
  if (!editing) return;

  const requestQty = Number(editing.qty);
  const oldQty = Number(editing.old_qty || 0);

  if (!Number.isFinite(requestQty) || requestQty <= 0) {
    alert("Qty must be greater than 0");
    return;
  }

  const product = getProductById(editing.product_id);

  const availableQty = Number(
    product?.available_qty ?? product?.qty ?? 0
  );

  const maxAllowedQty = oldQty + availableQty;

  if (requestQty > maxAllowedQty) {
    alert(
      `Only ${availableQty} extra item(s) available. Current qty is ${oldQty}, so maximum allowed qty is ${maxAllowedQty}.`
    );
    return;
  }

  try {
    setSaving(true);

    await api.put(`/alloc/${editing.id}`, {
      product_id: Number(editing.product_id),
      unit_id: Number(editing.unit_id || selectedUnit),
      user_id: Number(editing.user_id),
      qty: requestQty,
      notes: editing.notes || "",
      allocated_at: editing.allocated_at,
    });

    setEditing(null);

    await fetchProducts(selectedUnit);
    await fetchUnitSummary();
    await fetchAllocationsByUnit(selectedUnit);

    alert("Allocation updated successfully");
  } catch (error) {
    console.error("Edit Allocation Error:", error);

    alert(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to update allocation"
    );
  } finally {
    setSaving(false);
  }
};

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("ALL");
    setBrandFilter("ALL");
    setUserFilter("ALL");
    setVendorFilter("ALL");
    setMonthFilter("ALL");
    setCurrentPage(1);
  };

  const openSignedUpload = (row) => {
    setUploadingSignedFor(row);
    setSignedFiles([]);
  };

  const submitSignedUpload = async () => {
    if (!uploadingSignedFor?.id) return;

    if (!signedFiles.length) {
      alert("Please select at least one signed copy");
      return;
    }

    const fd = new FormData();

    signedFiles.forEach((file) => {
      fd.append("signed_documents", file);
    });

    try {
      setUploadingSigned(true);

      await api.post(
        `/alloc/${uploadingSignedFor.id}/signed-documents`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      alert("Signed copy uploaded successfully");

      setUploadingSignedFor(null);
      setSignedFiles([]);

      // Same courtesy-refresh rule as refreshAllocationView() above: the
      // upload already succeeded (and the modal already closed) by this
      // point, so a refetch hiccup here must not surface as an upload
      // failure.
      if (selectedUnit) {
        try {
          await fetchAllocationsByUnit(selectedUnit);
        } catch (refreshError) {
          console.error("Post-upload refresh failed (upload itself already succeeded):", refreshError);
        }
      }
    } catch (error) {
      console.error("Signed Copy Upload Error:", error);
      alert(getBackendErrorMessage(error, "Failed to upload signed copy"));
    } finally {
      setUploadingSigned(false);
    }
  };

  const fetchSignedDocuments = async (allocationId) => {
    try {
      setLoadingSignedDocs(true);

      const { data } = await api.get(
        `/alloc/${allocationId}/signed-documents`
      );

      const rows = Array.isArray(data) ? data : [];
      setSignedDocs(rows);
      setPreviewSignedDoc(rows[0] || null);

      return rows;
    } catch (error) {
      console.error("Fetch Signed Documents Error:", error);
      alert(error?.response?.data?.error || "Failed to fetch signed documents");
      setSignedDocs([]);
      setPreviewSignedDoc(null);
      return [];
    } finally {
      setLoadingSignedDocs(false);
    }
  };

  const openSignedPreview = async (row) => {
    if (!row?.id) return;

    setViewingSignedFor(row);
    setSignedDocs([]);
    setPreviewSignedDoc(null);

    await fetchSignedDocuments(row.id);
  };

  const closeSignedPreview = () => {
    setViewingSignedFor(null);
    setSignedDocs([]);
    setPreviewSignedDoc(null);
  };

  const getStatusBadge = (status) => <StatusChip status={status} />;

  const getOrg = (row = null) => {
    const source = row || allocations.find((item) => item.company_name);

    return {
      name: source?.company_name || "Company Name",
      address: source?.company_address || "-",
      phone: source?.company_phone || "-",
      email: source?.company_email || "-",
    };
  };

  const esc = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const formatDate = (date) => {
    if (!date) return "-";

    const dt = date instanceof Date ? date : new Date(date);

    if (Number.isNaN(dt.getTime())) return String(date);

    return dt.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const pad2 = (number) => String(number).padStart(2, "0");

  // Reads the Y/M/D calendar triple directly out of a 'YYYY-MM-DD...' string
  // rather than round-tripping through `new Date()` -- allocated_at is sent
  // by the backend as a plain date-only string, and parsing that via `Date`
  // then reading local getters can shift the date by a day in timezones
  // west of UTC. Falls back to Date parsing only for non-ISO inputs.
  const toDateInputValue = (value) => {
    if (!value) return "";
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  };

  const todayInputDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  };

  // Phase 0B: the old getApiBaseUrl()/getFileUrl() helpers built a direct,
  // unauthenticated `/uploads/...` URL. Signed documents are now fetched as
  // an authenticated Blob (see viewAuthenticatedFile in src/utils/fileAccess.js)
  // and exposed as a short-lived local object URL instead — the real API URL
  // (and the JWT that fetched it) never appears in the DOM.
  useEffect(() => {
    if (!previewSignedDoc?.id) {
      setPreviewObjectUrl(null);
      setPreviewError("");
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError("");

    let currentRevoke = null;

    viewAuthenticatedFile(`/files/allocation-documents/${previewSignedDoc.id}/view`)
      .then(({ objectUrl, revoke }) => {
        if (cancelled) {
          revoke();
          return;
        }
        currentRevoke = revoke;
        setPreviewObjectUrl(objectUrl);
      })
      .catch((err) => {
        if (cancelled) return;
        setPreviewError(getFileAccessErrorMessage(err));
        setPreviewObjectUrl(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
      if (currentRevoke) currentRevoke();
    };
  }, [previewSignedDoc?.id]);

  const handleDownloadSignedDoc = async () => {
    if (!previewSignedDoc?.id) return;
    try {
      await downloadAuthenticatedFile(
        `/files/allocation-documents/${previewSignedDoc.id}/download`,
        previewSignedDoc.original_name || getFileName(previewSignedDoc.file_path)
      );
    } catch (err) {
      alert(getFileAccessErrorMessage(err));
    }
  };

  const getFileName = (filePath, fallback = "Signed Copy") => {
    const source = filePath || fallback;

    try {
      return decodeURIComponent(String(source).split("/").pop() || fallback);
    } catch (_e) {
      return String(source).split("/").pop() || fallback;
    }
  };

  const isPdfFile = (filePath) =>
    String(filePath || "").toLowerCase().includes(".pdf");

  const isImageFile = (filePath) =>
    /\.(png|jpg|jpeg|webp|gif)$/i.test(String(filePath || ""));

  const exportCSV = () => {
    if (!filtered.length) {
      alert("No data available to export");
      return;
    }

    const headers = [
      "ID",
      "SKU",
      "Product",
      "Serial No",
      "Unit",
      "User",
      "Company",
      "Qty",
      "Status",
      "Notes",
      "Issue Date",
      "Return Date",
      "Model",
      "Specification",
    ];

    const rows = filtered.map((row) => [
      row.id,
      row.sku,
      row.product_name,
      row.serial_no || "",
      row.unit_name,
      row.user_name,
      row.company_name || "",
      row.qty,
      row.status,
      row.notes,
      row.allocated_at,
      row.return_at,
      row.model_no || "",
      specLine(row),
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((value) => csvQuote(value)).join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    const unitPart = sanitizeFileNamePart(selectedUnitInfo?.name);
    const monthPart = monthFileSuffix(monthFilter);

    link.href = url;
    link.download = `technician-allocations-${unitPart}-${monthPart}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

// mode "return": the Return Sheet of a RETURNED allocation -- Return Date,
// Returned By / Received By signatures and a return note instead of the
// allotment wording. Every other print keeps the allotment sheet as is.
const buildSignSheetHTML = (
  rows,
  title = "Unit Wise Product Allocation Sign Sheet",
  { mode = "allotment" } = {}
) => {
  const isReturn = mode === "return";
  const today = formatDate(new Date());
  const org = getOrg(rows?.[0]);
  let authUser = {};

  try {
    const savedUser = localStorage.getItem("user");
    authUser = savedUser ? JSON.parse(savedUser) : {};
  } catch (e) {
    authUser = {};
  }

  const preparedBy =
    authUser?.name ||
    authUser?.username ||
    authUser?.email ||
    "Logged-in User";

  const itemCards = rows
    .map((row, index) => {
      return `
        <div class="item-card">
          <div class="item-title">
            <div>
              <span class="serial-badge">#${index + 1}</span>
              <b>${esc(row.product_name || "-")}</b>
            </div>
            <div class="status-pill">${esc(row.status || "-")}</div>
          </div>

          <div class="info-grid">
            <div class="info-box">
              <span>Serial No</span>
              <b>${esc(row.serial_no || "-")}</b>
            </div>

            <div class="info-box">
              <span>Model</span>
              <b>${esc(row.model_no || "-")}</b>
            </div>

            <div class="info-box">
              <span>Specification</span>
              <b>${esc(specLine(row))}</b>
            </div>

            <div class="info-box">
              <span>Unit</span>
              <b>${esc(row.unit_name || selectedUnitInfo?.name || "-")}</b>
            </div>

            <div class="info-box">
              <span>User</span>
              <b>${esc(row.user_name || "-")}</b>
            </div>

            <div class="info-box full">
              <span>Company</span>
              <b>${esc(row.company_name || org.name || "-")}</b>
            </div>

            <div class="info-box">
              <span>Quantity</span>
              <b>${Number(row?.qty ?? 0)}</b>
            </div>

            <div class="info-box">
              <span>Issue Date</span>
              <b>${esc(formatDate(row.allocated_at) || "-")}</b>
            </div>
${isReturn ? `
            <div class="info-box full">
              <span>Return Date</span>
              <b>${esc(formatDate(row.return_at) || "-")}</b>
            </div>
` : ""}
            <div class="info-box full">
              <span>Notes</span>
              <b>${esc(row.notes || "-")}</b>
            </div>
          </div>
        </div>
      `;
    })
    .join("");

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>${esc(title)}</title>

<style>
  @page {
    size: A4 portrait;
    margin: 10mm;
  }

  * {
    box-sizing: border-box;
  }

  html,
  body {
    margin: 0;
    padding: 0;
    font-family: Arial, sans-serif;
    color: #111827;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  body {
    font-size: 12px;
    background: #ffffff;
  }

  .print-box {
    width: 100%;
    border: 2px solid #111827;
    padding: 12px;
    overflow: visible;
  }

  .head {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
    border-bottom: 2px solid #111827;
    padding-bottom: 10px;
    margin-bottom: 10px;
  }

  .brand {
    font-size: 18px;
    font-weight: 900;
    line-height: 1.25;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .muted {
    font-size: 11px;
    color: #374151;
    line-height: 1.4;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .doc-row {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    margin-top: 6px;
    font-size: 11px;
    border-top: 1px solid #d1d5db;
    padding-top: 6px;
  }

  .doc-row div {
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .title {
    text-align: center;
    font-size: 16px;
    font-weight: 900;
    text-transform: uppercase;
    margin: 10px 0;
    line-height: 1.3;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .meta {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 10px;
    table-layout: fixed;
  }

  .meta td {
    border: 1px solid #111827;
    padding: 7px;
    font-size: 11px;
    vertical-align: top;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .item-card {
    border: 1.5px solid #111827;
    margin-top: 10px;
    padding: 10px;
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .item-title {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: flex-start;
    border-bottom: 1px solid #111827;
    padding-bottom: 7px;
    margin-bottom: 8px;
  }

  .item-title b {
    font-size: 13px;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .serial-badge {
    display: inline-block;
    border: 1px solid #111827;
    padding: 3px 7px;
    margin-right: 6px;
    font-size: 11px;
    font-weight: 900;
  }

  .status-pill {
    border: 1px solid #111827;
    padding: 4px 8px;
    font-size: 10px;
    font-weight: 900;
    white-space: nowrap;
  }

  .info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 7px;
  }

  .info-box {
    border: 1px solid #d1d5db;
    padding: 7px;
    min-height: 44px;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .info-box.full {
    grid-column: 1 / -1;
  }

  .info-box span {
    display: block;
    font-size: 9px;
    text-transform: uppercase;
    color: #6b7280;
    font-weight: 900;
    letter-spacing: 0.5px;
    margin-bottom: 3px;
  }

  .info-box b {
    display: block;
    font-size: 11px;
    line-height: 1.35;
    font-weight: 800;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .sign-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
    margin-top: 10px;
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .sign-card {
    border: 1px solid #111827;
    padding: 6px;
    min-height: 82px;
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .sign-card b {
    font-size: 10px;
    display: block;
    line-height: 1.2;
  }

  .label {
    font-size: 8px;
    color: #374151;
    margin-top: 1px;
    line-height: 1.2;
  }

  .sign-line {
    border-top: 1px solid #111827;
    margin-top: 18px;
  }

  .sign-card div {
    font-size: 9px;
    line-height: 1.2;
  }

  .date-line {
    font-size: 13px !important;
    font-weight: 900;
    margin-top: 4px;
    letter-spacing: 1px;
  }

  .foot {
    margin-top: 12px;
    border-top: 1px solid #111827;
    padding-top: 7px;
    font-size: 10px;
    line-height: 1.4;
    color: #374151;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  @media print {
    html,
    body {
      width: 210mm;
    }

    .print-box {
      page-break-inside: auto;
    }

    .item-card,
    .sign-grid,
    .sign-card {
      page-break-inside: avoid;
      break-inside: avoid;
    }
  }
</style>
</head>

<body>
  <div class="print-box">
    <div class="head">
      <div>
        <div class="brand">${esc(org.name)}</div>
        <div class="muted">${esc(org.address)}</div>
        <div class="muted">${esc(org.phone)} | ${esc(org.email)}</div>
      </div>

      <div class="doc-row">
        <div>Date: <b>${esc(today)}</b></div>
        <div>Doc #: __________________</div>
      </div>
    </div>

    <div class="title">${esc(title)}</div>

    <table class="meta">
      <tr>
        <td><b>Selected Unit:</b><br/>${esc(selectedUnitInfo?.name || "-")}</td>
        <td><b>Total Records:</b><br/>${rows.length}</td>
      </tr>
      <tr>
        <td colspan="2"><b>Prepared By:</b><br/>${esc(preparedBy)}</td>
      </tr>
    </table>

    ${itemCards}

    <div class="sign-grid">
      <div class="sign-card">
        <div><b>${isReturn ? "Returned By" : "Issued By"}</b></div>
        <div class="label">Name & Signature</div>
        <div class="sign-line"></div>
        <div class="label">Date</div>
        <div class="date-line">____ / ____ / ______</div>
      </div>

      <div class="sign-card">
        <div><b>Received By</b></div>
        <div class="label">Name & Signature</div>
        <div class="sign-line"></div>
        <div class="label">Date</div>
        <div class="date-line">____ / ____ / ______</div>
      </div>

      <div class="sign-card">
        <div><b>Approved By</b></div>
        <div class="label">Name & Signature</div>
        <div class="sign-line"></div>
        <div class="label">Date</div>
        <div class="date-line">____ / ____ / ______</div>
      </div>
    </div>

    <div class="foot">
      <b>Note:</b> ${isReturn
        ? "Items are returned in the condition mentioned above. Discrepancies must be reported within 24 hours."
        : "Items are allotted in the condition mentioned above. Discrepancies must be reported within 24 hours."}
    </div>
  </div>

  <script>
    window.onload = function(){
      try { window.focus(); } catch(e) {}
      setTimeout(function(){ window.print(); }, 300);
    };
  </script>
</body>
</html>`;
};
  const openPrintWindow = () => window.open("", "_blank", "width=1100,height=850");

  const writePrintWindow = (printWindow, html) => {
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    printWindow.onload = () => {
      try {
        printWindow.focus();
      } catch {}
      printWindow.print();
    };
  };

  const printRows = (rows, title) => {
    if (!rows?.length) {
      alert("No data available to print");
      return;
    }

    const html = buildSignSheetHTML(rows, title);
    const printWindow = openPrintWindow();

    if (!printWindow) {
      alert("Popup blocked. Please allow popups to print.");
      return;
    }

    writePrintWindow(printWindow, html);
  };

  // Single-row print: the backend decides which document this allocation may
  // print from its CURRENT status (GET /alloc/:id/print/allotment|return) --
  // a returned allocation never gets the allotment sheet, even if this
  // page's copy of the row is stale. The window is opened synchronously in
  // the click handler (so popup blockers allow it) and filled once the
  // document data arrives.
  const printAllocationDocument = async (row, kind) => {
    if (!row) return;
    const printWindow = openPrintWindow();
    if (!printWindow) {
      alert("Popup blocked. Please allow popups to print.");
      return;
    }

    try {
      const doc = await loadPrintDocument(api, row.id, kind);
      const merged = { ...row, ...(doc?.allocation || {}) };
      const html =
        doc?.document_type === "RETURN"
          ? buildReturnSheetHTML(merged, { unitName: selectedUnitInfo?.name })
          : buildSignSheetHTML([merged], doc?.title || "Product Allotment Sheet");
      writePrintWindow(printWindow, html);
    } catch (error) {
      try {
        printWindow.close();
      } catch {}
      const code = error?.response?.data?.code;
      console.error("Print document error:", error);
      if (code === "ALLOCATION_RETURNED_USE_RETURN_SHEET" || code === "ALLOCATION_CANCELLED" || code === "ALLOCATION_NOT_RETURNED") {
        alert(getBackendErrorMessage(error, "This document is not available for the allocation's current status."));
        // The row on screen was out of date -- reload so the right print action shows.
        await refreshAllocationView();
        return;
      }
      alert(getBackendErrorMessage(error, "Failed to prepare the print document"));
    }
  };

  const printSingle = (row) => printAllocationDocument(row, PRINT_KIND.ALLOTMENT);

  const printReturn = (row) => printAllocationDocument(row, PRINT_KIND.RETURN);

  // Bulk "Print All / Filtered" is an allotment sheet, so only active
  // (ALLOCATED) rows are printed: returned rows have their own Print Return
  // Sheet action, cancelled rows are voided issues with nothing to print.
  const printAll = () => {
    const rows = allotmentPrintableRows(filtered);
    const skipped = (filtered || []).length - rows.length;
    if (!rows.length && skipped) {
      alert("None of the selected allocations is currently issued (they are returned or cancelled). Use Print Return Sheet on a returned row.");
      return;
    }
    printRows(rows, "Product Allotment Sheet");
  };

  const printHistory = () => {
    if (!historyUser) return;

    printRows(
      userHistoryRows,
      `Allocation History - ${historyUser.user_name || "User"}`
    );
  };

  return (
     <div className="puw-page puws-page tech-ds">

       <div className="puw-header puws-header tech-page-header">
        <div>
          <div className="unit-page-label puws-breadcrumb tech-section-label">Allocation Management</div>
          <h2 className="puws-title tech-page-title">Unit Wise Allocation</h2>
          <p className="puws-subtitle tech-page-subtitle">
            Search, filter and manage allocation records unit-wise.
          </p>
        </div>

        <div className="unit-header-actions puws-header-actions tech-header-actions">
          <Button className="unit-btn unit-btn-light puws-btn puws-btn-ghost" onClick={refreshPage}>
            <i className="fa-solid fa-rotate" aria-hidden="true"></i>
            Refresh
          </Button>

          <Button className="unit-btn unit-btn-light puws-btn puws-btn-ghost" onClick={exportCSV}>
            <i className="fa-solid fa-file-export" aria-hidden="true"></i>
            Export CSV
          </Button>

          <Button className="unit-btn unit-btn-light puws-btn puws-btn-ghost" onClick={printAll}>
            <i className="fa-solid fa-print" aria-hidden="true"></i>
            Print {search || statusFilter !== "ALL" || brandFilter !== "ALL" || userFilter !== "ALL" || vendorFilter !== "ALL" || monthFilter !== "ALL" ? "Filtered" : "All"}
          </Button>

          <Button
            className="unit-btn unit-btn-primary puws-btn puws-btn-primary"
            onClick={openCreateModal}
            disabled={!selectedUnit}
          >
            <i className="fa-solid fa-plus" aria-hidden="true"></i>
            Allocate
          </Button>
        </div>
      </div>

      <div className="unit-stats-row unit-top-stats allocation-top-stats puws-stats">
        <div className="unit-stat-card puws-stat s1 alloc-stat-card">
          <span className="alloc-stat-icon" aria-hidden="true">
            <i className="fa-solid fa-clipboard-list"></i>
          </span>
          <span className="alloc-stat-text">
            <span className="puws-stat-lbl">Total Records</span>
            <strong className="puws-stat-val">{stats.totalRecords}</strong>
          </span>
        </div>

        <div className="unit-stat-card puws-stat s2 alloc-stat-card">
          <span className="alloc-stat-icon" aria-hidden="true">
            <i className="fa-solid fa-box-open"></i>
          </span>
          <span className="alloc-stat-text">
            <span className="puws-stat-lbl">Allocated</span>
            <strong className="puws-stat-val">{stats.allocated}</strong>
          </span>
        </div>

        <div className="unit-stat-card puws-stat s3 alloc-stat-card">
          <span className="alloc-stat-icon" aria-hidden="true">
            <i className="fa-solid fa-rotate-left"></i>
          </span>
          <span className="alloc-stat-text">
            <span className="puws-stat-lbl">Returned</span>
            <strong className="puws-stat-val">{stats.returned}</strong>
          </span>
        </div>

        <div className="unit-stat-card puws-stat s4 alloc-stat-card">
          <span className="alloc-stat-icon" aria-hidden="true">
            <i className="fa-solid fa-ban"></i>
          </span>
          <span className="alloc-stat-text">
            <span className="puws-stat-lbl">Cancelled</span>
            <strong className="puws-stat-val">{stats.cancelled}</strong>
          </span>
        </div>

        <div className="unit-stat-card puws-stat s5 alloc-stat-card">
          <span className="alloc-stat-icon" aria-hidden="true">
            <i className="fa-solid fa-boxes-stacked"></i>
          </span>
          <span className="alloc-stat-text">
            <span className="puws-stat-lbl">Allocated Qty</span>
            <strong className="puws-stat-val">{stats.allocatedQty}</strong>
          </span>
        </div>
      </div>

      <div className="unit-card puws-card">
        <div className="unit-toolbar puws-toolbar">
          <div className="puws-toolbar-left">
            <h5>
              Allocation List{" "}
              {selectedUnitInfo ? `- ${selectedUnitInfo.name}` : ""}
            </h5>
            <p>Search, filter, edit and update allocation status unit wise.</p>
          </div>

          <div className="unit-toolbar-actions puws-toolbar-right">
            <InputGroup className="unit-search puws-search-wrap">
              <InputGroup.Text>
                <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
              </InputGroup.Text>

              <Form.Control
                className="puws-search"
                placeholder="Search product, serial, user, company..."
                aria-label="Search allocations"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {search && (
                <Button
                  className="unit-clear-btn"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                  title="Clear search"
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                </Button>
              )}
            </InputGroup>

            <Form.Select
              className="unit-page-size puws-select alloc-filter-month"
              aria-label="Filter by month"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              title="Filter by allocation (issue) month"
            >
              <option value="ALL">All Months</option>
              {monthOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="unit-page-size puws-select alloc-filter-status"
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All Status</option>
              <option value="ALLOCATED">Allocated</option>
              <option value="RETURNED">Returned</option>
              <option value="CANCELLED">Cancelled</option>
            </Form.Select>

            <Form.Select
              className="unit-page-size puws-select alloc-filter-brand"
              aria-label="Filter by brand"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
            >
              <option value="ALL">All Brands</option>
              {brandOptions.map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="unit-page-size puws-select alloc-filter-user"
              aria-label="Filter by user"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
            >
              <option value="ALL">All Users</option>
              {userOptions.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="unit-page-size puws-select alloc-filter-company"
              aria-label="Filter by company"
              value={vendorFilter}
              onChange={(e) => setVendorFilter(e.target.value)}
            >
              <option value="ALL">All Companies</option>
              {vendorOptions.map((company) => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="unit-page-size puws-select alloc-filter-rows"
              aria-label="Rows per page"
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
            >
              <option value={5}>5 rows</option>
              <option value={10}>10 rows</option>
              <option value={20}>20 rows</option>
              <option value={50}>50 rows</option>
            </Form.Select>

            {(search || statusFilter !== "ALL" || brandFilter !== "ALL" || userFilter !== "ALL" || vendorFilter !== "ALL" || monthFilter !== "ALL") && (
              <Button
                className="unit-btn unit-btn-light puws-btn puws-btn-ghost alloc-reset-btn"
                onClick={clearFilters}
                aria-label="Reset filters"
                title="Reset filters"
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                <span className="alloc-reset-label">Reset</span>
              </Button>
            )}
          </div>
        </div>

        <div className="unit-table-wrapper puws-table-wrap">
          {loadingAllocations ? (
            <div className="unit-loader puws-loader">
              <Spinner animation="border" />
              <p>Loading unit allocations...</p>
            </div>
          ) : (
            <table className="puw-table puws-table alloc-table">
              <colgroup>
                <col className="alloc-col-sno" />
                <col className="alloc-col-product" />
                <col className="alloc-col-serial" />
                <col className="alloc-col-unit" />
                <col className="alloc-col-user" />
                <col className="alloc-col-company" />
                <col className="alloc-col-qty" />
                <col className="alloc-col-status" />
                <col className="alloc-col-date" />
                <col className="alloc-col-date" />
                <col className="alloc-col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th className="alloc-th-center">S.No</th>
                  <th>Product</th>
                  <th className="alloc-th-center">Serial No</th>
                  <th>Unit</th>
                  <th>User</th>
                  <th>Company</th>
                  <th className="alloc-th-center">Qty</th>
                  <th className="alloc-th-center">Status</th>
                  <th className="alloc-th-center">Issue Date</th>
                  <th className="alloc-th-center">Return Date</th>
                  <th className="alloc-th-center">Actions</th>
                </tr>
              </thead>

              <tbody>
                {pageData.length > 0 ? (
                  pageData.map((row, index) => (
                    <tr key={row.id}>
                      <td className="alloc-td-center">
                        <span className="unit-id puws-sno tech-sno">{indexOfFirst + index + 1}</span>
                      </td>

                      <td>
                        <div className="unit-name-box puws-prod-cell alloc-prod-cell">
                          <ProductTypeAvatar
                            name={row.product_name}
                            brand={row.brand}
                          />

                          <div className="alloc-prod-text">
                            <strong
                              className="puws-prod-name alloc-prod-name"
                              onMouseEnter={titleIfTruncated}
                            >
                              {row.product_name || "-"}
                            </strong>
                            <small
                              className="puws-prod-sku alloc-prod-sub"
                              onMouseEnter={titleIfTruncated}
                            >
                              {productMetaLine(row)}
                            </small>
                          </div>
                        </div>
                      </td>

                      <td className="alloc-td-center">
                        <span className="alloc-serial" onMouseEnter={titleIfTruncated}>
                          {row.serial_no || "-"}
                        </span>
                      </td>

                      <td>
                        <UnitChip name={row.unit_name} />
                      </td>

                      <td>
                        <UserChip name={row.user_name} />
                      </td>

                      <td className={row.company_name ? undefined : "alloc-td-center"}>
                        <span className="alloc-company" onMouseEnter={titleIfTruncated}>
                          {row.company_name || "-"}
                        </span>
                      </td>

                      <td className="alloc-td-center">
                        <QtyBadge value={row.qty} />
                      </td>

                      <td className="alloc-td-center">{getStatusBadge(row.status)}</td>

                      <td className="alloc-td-center">
                        <span className="alloc-date">{formatDate(row.allocated_at)}</span>
                      </td>
                      <td className="alloc-td-center">
                        <span className="alloc-date">
                          {row.return_at ? formatDate(row.return_at) : "-"}
                        </span>
                      </td>

                      <td>
                        <div className="unit-actions puws-actions">
                          <Button
                            className="unit-icon-btn unit-edit puws-action-btn edit"
                            title="Edit"
                            aria-label="Edit allocation"
                            onClick={() =>
                              setEditing({
                                ...row,
                                old_qty: Number(row.qty || 0),
                                allocated_at: toDateInputValue(row.allocated_at),
                              })
                            }
                          >
                            <i className="fa-solid fa-pen" aria-hidden="true"></i>
                          </Button>

                          <Button
                            className="unit-icon-btn unit-delete puws-action-btn delete"
                            title="Delete"
                            aria-label="Delete allocation"
                            onClick={() => remove(row.id)}
                          >
                            <i className="fa-solid fa-trash" aria-hidden="true"></i>
                          </Button>

                          <Button
                            className="unit-icon-btn unit-view puws-action-btn view"
                            title="Mark Returned"
                            aria-label="Mark as returned"
                            onClick={() => returnAllocation(row)}
                            disabled={row.status !== "ALLOCATED"}
                          >
                            <i className="fa-solid fa-rotate-left" aria-hidden="true"></i>
                          </Button>

                          <Button
                            className="unit-icon-btn unit-cancel puws-action-btn cancel"
                            title="Cancel Allocation"
                            aria-label="Cancel allocation"
                            onClick={() => setStatus(row, "CANCELLED")}
                            disabled={row.status === "CANCELLED"}
                          >
                            <i className="fa-solid fa-ban" aria-hidden="true"></i>
                          </Button>

                          {/* One print action per row: Allotment Sheet while
                              the product is on issue, Return Sheet once returned. */}
                          <AllocationPrintActions
                            row={row}
                            onPrintAllotment={printSingle}
                            onPrintReturn={printReturn}
                          />

                          <Button
                            className="unit-icon-btn unit-upload puws-action-btn upload"
                            title="Upload Signed Copy"
                            aria-label="Upload signed copy"
                            onClick={() => openSignedUpload(row)}
                          >
                            <i className="fa-solid fa-paperclip" aria-hidden="true"></i>
                            {Number(row.signed_document_count || 0) > 0 && (
                              <span className="alloc-count-badge">
                                {row.signed_document_count}
                              </span>
                            )}
                          </Button>

                          <Button
                            className="unit-icon-btn unit-view puws-action-btn view"
                            title="Preview Signed Copy"
                            aria-label="Preview signed copy"
                            onClick={() => openSignedPreview(row)}
                            disabled={!Number(row.signed_document_count || 0)}
                          >
                            <i className="fa-solid fa-eye" aria-hidden="true"></i>
                          </Button>

                          <Button
                            className="unit-icon-btn unit-history puws-action-btn history"
                            title="User History"
                            aria-label="View user history"
                            onClick={() => setHistoryUser(row)}
                          >
                            <i className="fa-solid fa-clock-rotate-left" aria-hidden="true"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11">
                      <div className="unit-empty puws-empty">
                        <div className="unit-empty-icon puws-empty-icon">
                          <i className="fa-solid fa-inbox" aria-hidden="true"></i>
                        </div>
                        <h5>No allocation found</h5>
                        <p>
                          {monthFilter !== "ALL"
                            ? `No records found for ${monthOptions.find((o) => o.value === monthFilter)?.label || monthFilter}.`
                            : "Select another unit or allocate a new product."}
                        </p>

                        <Button
                          className="unit-btn unit-btn-primary puws-btn puws-btn-primary"
                          onClick={openCreateModal}
                          disabled={!selectedUnit}
                        >
                          <i className="fa-solid fa-plus" aria-hidden="true"></i>
                          Allocate Product
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="unit-footer puws-footer">
          <div className="unit-count-text">
            Showing{" "}
            <strong>{filtered.length === 0 ? 0 : indexOfFirst + 1}</strong> to{" "}
            <strong>{Math.min(indexOfLast, filtered.length)}</strong> of{" "}
            <strong>{filtered.length}</strong> entries
          </div>

          {totalPages > 1 && (
            <Pagination className="unit-pagination puws-page-btns">
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              />

              {compactPageItems([...Array(totalPages)].map((_, index) => (
                <Pagination.Item
                  key={index + 1}
                  active={index + 1 === currentPage}
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              )), currentPage)}

              <Pagination.Next
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
              />
            </Pagination>
          )}
        </div>
      </div>

      <Modal
        show={showCreate}
        onHide={() => {
          setShowCreate(false);
          resetCreateForm();
        }}
        centered
        size="lg"
        className="unit-modal allocation-create-modal alloc-modal tech-modal"
      >
        <Modal.Header closeButton>
          <TechModalTitle icon="fa-box-open">
            Allocate Product to {selectedUnitInfo?.name || "Selected Unit"}
          </TechModalTitle>
        </Modal.Header>

        <Modal.Body>
          <div className="alert alert-light border mb-3">
            <strong>Selected Unit:</strong> {selectedUnitInfo?.name || "-"}
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>User</Form.Label>

                <div className="alloc-search-select">
                  <Form.Control
                    value={userSearch}
                    placeholder="Search user..."
                    autoComplete="off"
                    onFocus={() => setShowUserDrop(true)}
                    onChange={(e) => {
                      setUserSearch(e.target.value);
                      setUserId("");
                      setShowUserDrop(true);
                    }}
                  />

                  {userId && (
                    <button
                      type="button"
                      className="alloc-clear"
                      onClick={() => {
                        setUserId("");
                        setUserSearch("");
                        setShowUserDrop(false);
                      }}
                    >
                      ×
                    </button>
                  )}

                  {showUserDrop && (
                    <div className="alloc-dropdown">
                      {filteredUsers.length ? (
                        filteredUsers.map((user) => (
                          <div
                            key={user.id}
                            className="alloc-option"
                            onMouseDown={() => selectUser(user)}
                          >
                            <strong>{user.name}</strong>
                            <small>
                              {user.email || user.username || user.phone_no || ""}
                            </small>
                          </div>
                        ))
                      ) : (
                        <div className="alloc-empty-option">No user found</div>
                      )}
                    </div>
                  )}
                </div>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Qty</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max={selectedProductGroup ? getGroupAvailableAfterCart(selectedProductGroup) : undefined}
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                />

                {selectedProductGroup && (
                  <small className="text-muted">
                    Available after added list: {getGroupAvailableAfterCart(selectedProductGroup)}
                  </small>
                )}
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>
                  Allocation Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  max={todayInputDate()}
                  value={allocatedAt}
                  onChange={(e) => setAllocatedAt(e.target.value)}
                />
              </Form.Group>
            </div>

            <div className="col-md-12">
              <Form.Group>
                <Form.Label>Product</Form.Label>

                <div className="alloc-search-select">
                  <Form.Control
                    value={productSearch}
                    placeholder="Search product, serial, model, SKU..."
                    autoComplete="off"
                    onFocus={() => setShowProductDrop(true)}
                    onChange={(e) => {
                      setProductSearch(e.target.value);
                      setProductId("");
                      setShowProductDrop(true);
                    }}
                  />

                  {productId && (
                    <button
                      type="button"
                      className="alloc-clear"
                      onClick={() => {
                        setProductId("");
                        setProductSearch("");
                        setShowProductDrop(false);
                      }}
                    >
                      ×
                    </button>
                  )}

                  {showProductDrop && (
                    <div className="alloc-dropdown product-dropdown">
                      {filteredProductGroups.length ? (
                        filteredProductGroups.map((group) => (
                          <div
                            key={group.key}
                            className="alloc-option product-option"
                            onMouseDown={() => selectProductGroup(group)}
                          >
                            <div>
                              <strong>{group.name}</strong>
                              <small>
                                Model: {group.model_no || "-"} • Brand:{" "}
                                {group.brand || "-"} • {specLine(group)}
                              </small>
                              <small>
                              <strong>SKU:</strong> {group.skuPreview} •{" "}
                              <strong>Serial:</strong> {group.serialPreview} •{" "}
                              <strong>Purchase Date:</strong>{" "}
                              {formatDate(group.purchase_date)}
                            </small>
                            </div>

                            <span className="alloc-qty-pill">
                            Available {group.cart_available_qty}
                          </span>
                          </div>
                        ))
                      ) : (
                        <div className="alloc-empty-option">
                          No available product found
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {selectedProductGroup && (
                  <div className="selected-product-box mt-2">
                    <div>
                      <strong>{selectedProductGroup.name}</strong>
                      <small>
                        Available Qty: {getGroupAvailableAfterCart(selectedProductGroup)} •
                        Total Qty: {selectedProductGroup.total_qty}
                      </small>
                    </div>
                    <div>
                      <small>
                        Serial: {selectedProductGroup.serialPreview}
                      </small>
                      <small>Model: {selectedProductGroup.model_no || "-"}</small>
                      <small>
                        Purchase Date: {formatDate(selectedProductGroup.purchase_date)}
                      </small>
                    </div>
                  </div>
                )}
              </Form.Group>
            </div>

            <div className="col-12">
              <Button
                type="button"
                className="unit-btn unit-btn-light w-100"
                onClick={addAllocationItem}
                disabled={!userId || !selectedProductGroup || !qty}
              >
                + Add Item to List
              </Button>
            </div>

            {allocationItems.length > 0 && (
              <div className="col-12">
                <div className="allocation-items-box">
                  <div className="allocation-items-header">
                    <strong>Selected Items</strong>
                    <span>{allocationItems.length} item(s)</span>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-bordered table-sm allocation-items-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Product</th>
                          <th>SKU / Serial</th>
                          <th>Model</th>
                          <th>Qty</th>
                          <th>Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {allocationItems.map((item, index) => (
                          <tr key={`${item.product_id}-${index}`}>
                            <td>{index + 1}</td>
                            <td>
                              <strong>{item.product_name}</strong>
                              <br />
                              <small>{specLine(item)}</small>
                            </td>
                            <td>
                              <strong>{item.sku || "-"}</strong>
                              <br />
                              <small>{item.serial_no || "-"}</small>
                            </td>
                            <td>{item.model_no || "-"}</td>
                            <td>
                              <span className="unit-id">{item.qty}</span>
                            </td>
                            <td>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline-danger"
                                onClick={() => removeAllocationItem(index)}
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            <div className="col-12">
              <Form.Group>
                <Form.Label>Common Notes</Form.Label>
                <Form.Control
                  placeholder="Any remarks..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="unit-btn unit-btn-light"
            onClick={() => {
              setShowCreate(false);
              resetCreateForm();
            }}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            className="unit-btn unit-btn-primary"
            onClick={submit}
            disabled={saving || !allocationItems.length}
          >
            {saving ? "Allocating..." : `Allocate All (${allocationItems.length})`}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!editing}
        onHide={() => setEditing(null)}
        centered
        className="unit-modal alloc-modal tech-modal"
      >
        <Modal.Header closeButton>
          <TechModalTitle icon="fa-pen-to-square">Edit Allocation</TechModalTitle>
        </Modal.Header>

        <Modal.Body>
          <div className="alert alert-light border mb-3">
            <strong>Unit:</strong> {editing?.unit_name || "-"} <br />
            <strong>User:</strong> {editing?.user_name || "-"} <br />
            <strong>Product:</strong> {editing?.sku || "-"} —{" "}
            {editing?.product_name || "-"} <br />
            <strong>Serial No:</strong> {editing?.serial_no || "-"} <br />
            <strong>Company:</strong> {editing?.company_name || "-"}
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Qty</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={editing?.qty || ""}
              onChange={(e) =>
                setEditing({
                  ...editing,
                  qty: e.target.value,
                })
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Allocation Date <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              max={todayInputDate()}
              value={editing?.allocated_at || ""}
              onChange={(e) =>
                setEditing({
                  ...editing,
                  allocated_at: e.target.value,
                })
              }
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Notes</Form.Label>
            <Form.Control
              value={editing?.notes || ""}
              onChange={(e) =>
                setEditing({
                  ...editing,
                  notes: e.target.value,
                })
              }
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="unit-btn unit-btn-light"
            onClick={() => setEditing(null)}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            className="unit-btn unit-btn-primary"
            onClick={saveEdit}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!historyUser}
        onHide={() => setHistoryUser(null)}
        centered
        size="lg"
        className="unit-modal alloc-modal tech-modal"
      >
        <Modal.Header closeButton>
          <TechModalTitle icon="fa-clock-rotate-left">
            Allocation History - {historyUser?.user_name || "-"}
          </TechModalTitle>
        </Modal.Header>

        <Modal.Body>
          <div className="alert alert-light border mb-3">
            <strong>User:</strong> {historyUser?.user_name || "-"} <br />
            <strong>Company:</strong> {historyUser?.company_name || "-"} <br />
            <strong>Total Items:</strong> {userHistoryRows.length}
          </div>

          <div className="table-responsive">
            <table className="table unit-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Product</th>
                  <th>Serial No</th>
                  <th>Qty</th>
                  <th>Status</th>
                  <th>Issue Date</th>
                  <th>Return Date</th>
                </tr>
              </thead>

              <tbody>
                {userHistoryRows.length > 0 ? (
                  userHistoryRows.map((item, index) => (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td>
                        <strong>
                          {item.sku} — {item.product_name}
                        </strong>
                        <br />
                        <small>
                          M: {item.model_no || "-"} • {specLine(item)}
                        </small>
                      </td>
                      <td>{item.serial_no || "-"}</td>
                      <td>{item.qty}</td>
                      <td>{getStatusBadge(item.status)}</td>
                      <td>{item.allocated_at || "-"}</td>
                      <td>{item.return_at || "-"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No history found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="unit-btn unit-btn-light"
            onClick={() => setHistoryUser(null)}
          >
            Close
          </Button>

          <Button
            className="unit-btn unit-btn-primary"
            onClick={printHistory}
            disabled={!userHistoryRows.length}
          >
            Print History
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!uploadingSignedFor}
        onHide={() => {
          setUploadingSignedFor(null);
          setSignedFiles([]);
        }}
        centered
        className="unit-modal alloc-modal tech-modal"
      >
        <Modal.Header closeButton>
          <TechModalTitle icon="fa-file-arrow-up">Upload Signed Copy</TechModalTitle>
        </Modal.Header>

        <Modal.Body>
          <div className="alert alert-light border mb-3">
            <strong>Product:</strong> {uploadingSignedFor?.product_name || "-"} <br />
            <strong>User:</strong> {uploadingSignedFor?.user_name || "-"} <br />
            <strong>Allowed:</strong> PDF, JPG, JPEG, PNG. Max 10 MB per file.
          </div>

          <Form.Group>
            <Form.Label>Signed Copy Files</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                const overLimit = files.find(
                  (file) => file.size > 10 * 1024 * 1024
                );

                if (overLimit) {
                  alert("Each file must be 10 MB or less");
                  e.target.value = "";
                  setSignedFiles([]);
                  return;
                }

                setSignedFiles(files);
              }}
            />
          </Form.Group>

          {signedFiles.length > 0 && (
            <div className="mt-3">
              <strong>Selected Files:</strong>
              <ul className="mb-0 mt-2">
                {signedFiles.map((file) => (
                  <li key={`${file.name}-${file.size}`}>{file.name}</li>
                ))}
              </ul>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="unit-btn unit-btn-light"
            onClick={() => {
              setUploadingSignedFor(null);
              setSignedFiles([]);
            }}
            disabled={uploadingSigned}
          >
            Cancel
          </Button>

          <Button
            className="unit-btn unit-btn-primary"
            onClick={submitSignedUpload}
            disabled={uploadingSigned}
          >
            {uploadingSigned ? "Uploading..." : "Upload"}
          </Button>
        </Modal.Footer>
      </Modal>


      <Modal
        show={!!viewingSignedFor}
        onHide={closeSignedPreview}
        centered
        size="xl"
        className="unit-modal signed-preview-modal alloc-modal tech-modal"
      >
        <Modal.Header closeButton>
          <TechModalTitle icon="fa-file-signature">Signed Copy Preview</TechModalTitle>
        </Modal.Header>

        <Modal.Body>
          <div className="alert alert-light border mb-3">
            <strong>Product:</strong> {viewingSignedFor?.product_name || "-"} <br />
            <strong>User:</strong> {viewingSignedFor?.user_name || "-"} <br />
            <strong>Total Uploaded Files:</strong> {signedDocs.length}
          </div>

          {loadingSignedDocs ? (
            <div className="unit-loader">
              <Spinner animation="border" />
              <p>Loading signed documents...</p>
            </div>
          ) : signedDocs.length > 0 ? (
            <div className="signed-preview-layout">
              <div className="signed-doc-list">
                <h6>Uploaded Files</h6>

                {signedDocs.map((doc) => (
                  <button
                    type="button"
                    key={doc.id}
                    className={`signed-doc-item ${
                      String(previewSignedDoc?.id) === String(doc.id)
                        ? "active"
                        : ""
                    }`}
                    onClick={() => setPreviewSignedDoc(doc)}
                  >
                    <strong>
                      {doc.original_name ||
                        getFileName(doc.file_path, `Signed Copy ${doc.id}`)}
                    </strong>
                    <small>
                      {doc.uploaded_at ? `Uploaded: ${doc.uploaded_at}` : ""}
                    </small>
                  </button>
                ))}
              </div>

              <div className="signed-preview-box">
                {previewSignedDoc ? (
                  <>
                    <div className="signed-preview-head">
                      <strong>
                        {previewSignedDoc.original_name ||
                          getFileName(previewSignedDoc.file_path)}
                      </strong>

                      <button
                        type="button"
                        onClick={handleDownloadSignedDoc}
                        className="unit-btn unit-btn-light"
                        disabled={previewLoading}
                      >
                        Open / Download
                      </button>
                    </div>

                    {previewLoading ? (
                      <div className="unit-loader">
                        <Spinner animation="border" />
                        <p>Loading preview...</p>
                      </div>
                    ) : previewError ? (
                      <div className="unit-empty">
                        <div className="unit-empty-icon">!</div>
                        <h5>Unable to load preview</h5>
                        <p className="text-danger">{previewError}</p>
                      </div>
                    ) : isImageFile(previewSignedDoc.file_path) && previewObjectUrl ? (
                      <img
                        src={previewObjectUrl}
                        alt={previewSignedDoc.original_name || "Signed Copy"}
                        className="signed-preview-image"
                      />
                    ) : isPdfFile(previewSignedDoc.file_path) && previewObjectUrl ? (
                      <iframe
                        title={previewSignedDoc.original_name || "Signed Copy PDF"}
                        src={previewObjectUrl}
                        className="signed-preview-frame"
                      />
                    ) : (
                      <div className="unit-empty">
                        <div className="unit-empty-icon">□</div>
                        <h5>Preview not available</h5>
                        <p>Please open/download this file to view it.</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="unit-empty">
                    <div className="unit-empty-icon">□</div>
                    <h5>Select a file</h5>
                    <p>Choose an uploaded signed copy from the left side.</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="unit-empty">
              <div className="unit-empty-icon">□</div>
              <h5>No signed copy uploaded</h5>
              <p>Please upload a signed copy first.</p>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={closeSignedPreview}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .allocation-create-modal .modal-dialog {
          max-width: 900px;
        }

        .alloc-search-select {
          position: relative;
        }

        .alloc-search-select .form-control {
          height: 48px;
          border-radius: 14px;
          border: 1px solid #dbe3ef;
          font-size: 14px;
          font-weight: 600;
          padding-right: 42px;
          box-shadow: none !important;
        }

        .alloc-search-select .form-control:focus {
          border-color: var(--primary, #2d7b48);
          box-shadow: 0 0 0 3px rgba(45, 123, 72, 0.12) !important;
        }

        .alloc-clear {
          position: absolute;
          right: 10px;
          top: 9px;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          border: none;
          background: #f1f5f9;
          color: #334155;
          font-weight: 900;
          z-index: 12;
        }

        .alloc-dropdown {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          right: 0;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          box-shadow: 0 20px 45px rgba(15, 23, 42, 0.18);
          max-height: 260px;
          overflow: auto;
          z-index: 9999;
          padding: 6px;
        }

        .product-dropdown {
          max-height: 320px;
        }

        .alloc-option {
          padding: 10px 12px;
          border-radius: 12px;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .alloc-option:hover {
          background: var(--info-bg, #eff6ff);
        }

        .alloc-option strong {
          font-size: 14px;
          color: #14151f;
          font-weight: 900;
        }

        .alloc-option small {
          font-size: 12px;
          color: #64748b;
          font-weight: 600;
        }

        .product-option {
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .alloc-qty-pill {
          flex-shrink: 0;
          padding: 6px 10px;
          border-radius: 999px;
          background: #dcfce7;
          color: #166534;
          font-size: 12px;
          font-weight: 900;
        }

        .alloc-empty-option {
          padding: 14px;
          text-align: center;
          color: #64748b;
          font-size: 13px;
          font-weight: 700;
        }

        .selected-product-box {
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          background: #f8fafc;
          padding: 12px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }

        .selected-product-box strong {
          display: block;
          color: #14151f;
          font-size: 14px;
          font-weight: 900;
        }

        .selected-product-box small {
          display: block;
          color: #64748b;
          font-size: 12px;
          font-weight: 700;
          margin-top: 2px;
        }

        .allocation-items-box {
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          background: #ffffff;
          padding: 12px;
        }

        .allocation-items-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
        }

        .allocation-items-header strong {
          color: #14151f;
          font-size: 14px;
          font-weight: 900;
        }

        .allocation-items-header span {
          background: #fbeef1;
          color: #6d1530;
          border-radius: 999px;
          padding: 5px 10px;
          font-size: 12px;
          font-weight: 900;
        }

        .allocation-items-table {
          margin-bottom: 0;
          font-size: 13px;
        }

        .allocation-items-table th {
          background: #f8fafc;
          color: #475569;
          font-size: 12px;
          font-weight: 900;
          white-space: nowrap;
        }

        .allocation-items-table td {
          vertical-align: middle;
        }

        .allocation-items-table small {
          color: #64748b;
          font-weight: 600;
        }


        .signed-doc-list {
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 12px;
          background: #f8fafc;
          max-height: 560px;
          overflow: auto;
        }

        .signed-doc-list h6 {
          font-size: 13px;
          font-weight: 900;
          color: #14151f;
          margin-bottom: 10px;
        }

        .signed-doc-item {
          width: 100%;
          border: 1px solid #e2e8f0;
          background: #ffffff;
          border-radius: 12px;
          padding: 10px;
          text-align: left;
          margin-bottom: 8px;
          cursor: pointer;
        }

        .signed-doc-item.active,
        .signed-doc-item:hover {
          border-color: var(--primary, #2d7b48);
          background: #eaf5ee;
        }

        .signed-doc-item strong {
          display: block;
          font-size: 13px;
          font-weight: 900;
          color: #14151f;
          overflow-wrap: anywhere;
        }

        .signed-doc-item small {
          display: block;
          margin-top: 3px;
          font-size: 11px;
          color: #64748b;
          font-weight: 600;
        }

        .signed-preview-box {
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          background: #ffffff;
          padding: 12px;
          min-height: 520px;
        }

        .signed-preview-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 10px;
          margin-bottom: 12px;
        }

        .signed-preview-head strong {
          font-size: 14px;
          font-weight: 900;
          color: #14151f;
          overflow-wrap: anywhere;
        }

        .signed-preview-frame {
          width: 100%;
          height: 480px;
          border: 0;
          border-radius: 12px;
          background: #f8fafc;
        }

        .signed-preview-image {
          width: 100%;
          max-height: 480px;
          object-fit: contain;
          border-radius: 12px;
          background: #f8fafc;
        }

        @media (max-width: 768px) {
          .selected-product-box,
          .signed-preview-layout {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
    
  );
}